import { FC, useMemo } from 'react';
import Typography from '@components/common/typography';
import Button from '@components/common/button';
import styles from './styles.module.scss';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { ETwoFAMethod } from '@xeppt/xeppt-sdk/types';
import { useLocales } from '@hooks/helpers/useLocales';
import VerificationCode from '@components/verification_code';
import FormField from '@components/form_field';

interface IProps {
    method: ETwoFAMethod;
    control: any;
    isLoading: boolean;
    onSubmit: () => void;
    onFallback: () => void;
}

const MFALayout: FC<IProps> = ({ method, control, isLoading, onSubmit, onFallback }) => {
    const { submitLocale } = useLocales();
    const renderTexts = useMemo(() => {
        switch (method) {
            case ETwoFAMethod.EMAIL:
                return {
                    title: 'We just sent you an email',
                    description:
                        'To sign in, please enter the 6-digit verification code we sent to your primary email. The code expires in 5 minutes',
                    helper: '',
                    linkText: ''
                };
            case ETwoFAMethod.SMS:
                return {
                    title: 'We just sent you an SMS',
                    description:
                        'To sign in, please enter the 6-digit verification code we sent to your primary phone number. The code expires in 5 minutes',
                    helper: '',
                    linkText: ''
                };
            case ETwoFAMethod.TOTP:
                return {
                    title: 'Check your authenticator app',
                    description:
                        'To sign in, please enter the 6-digit verification code shown in your authenticator app',
                    helper: 'Can’t find your device?',
                    linkText: 'Use SMS verification'
                };
        }
    }, []);

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <Typography className={styles.title} variant="h3">
                {renderTexts?.title}
            </Typography>
            <Typography className={styles.description} variant="body2">
                {renderTexts?.description}
            </Typography>
            <div className={styles.form}>
                <FormField
                    control={control}
                    name="mfaCode"
                    renderComponent={(props) => <VerificationCode onSubmit={onSubmit} {...props} />}
                />
                {renderTexts?.helper && (
                    <Typography variant="body3" className={styles.details}>
                        {renderTexts?.helper}{' '}
                        <button onClick={onFallback}>{renderTexts?.linkText}</button>
                    </Typography>
                )}
            </div>
            <Button onClick={onSubmit} isLoading={isLoading} variant="primary">
                {submitLocale('verify')}
            </Button>
        </motion.div>
    );
};
export default MFALayout;
